"use client";

import { isMarketingLandingHashSectionId } from "@marketing/shared/lib/marketing-landing-hash-sections";
import { replaceMarketingLandingHash } from "@marketing/shared/lib/marketing-landing-home-sections";
import { MARKETING_SITE_HEADER_HEIGHT_VAR } from "@marketing/shared/lib/marketing-layout-vars";
import { scrollToSmoothRespectingMotion } from "@shared/lib/scroll-to-smooth-respecting-motion";

function marketingStickyHeaderOffsetPx(): number {
	const raw = getComputedStyle(document.documentElement)
		.getPropertyValue(MARKETING_SITE_HEADER_HEIGHT_VAR)
		.trim();
	const fromVar = Number.parseFloat(raw);
	if (Number.isFinite(fromVar) && fromVar > 0) {
		return Math.ceil(fromVar);
	}
	const header = document.querySelector("[data-marketing-site-header]");
	return header instanceof HTMLElement
		? Math.ceil(header.getBoundingClientRect().height)
		: 0;
}

function resolveMarketingAnchorDomId(id: string): string {
	return id === "cta" ? "earlyaccess" : id;
}

/**
 * Scroll so `{ id }` aligns under the sticky marketing header (`data-marketing-site-header`).
 * Reads `--marketing-site-header-height` when set by `MarketingSiteHeader`; falls back to a live header measurement.
 * Uses {@link replaceMarketingLandingHash} so hash updates don't stack browser history entries.
 * Honors `prefers-reduced-motion` via {@link scrollToSmoothRespectingMotion}.
 */
export function scrollToMarketingAnchor(id: string, hashHref?: string): void {
	if (typeof window === "undefined") {
		return;
	}
	const domId = resolveMarketingAnchorDomId(id);
	const el = window.document.getElementById(domId);
	if (!el) {
		return;
	}

	const run = () => {
		const offset = marketingStickyHeaderOffsetPx();
		const top = el.getBoundingClientRect().top + window.scrollY - offset;
		scrollToSmoothRespectingMotion({
			top: Math.max(0, top),
			behavior: "smooth",
		});
		if (hashHref != null && isMarketingLandingHashSectionId(id)) {
			replaceMarketingLandingHash(id === "cta" ? "earlyaccess" : id);
		}
	};

	window.requestAnimationFrame(() => {
		window.requestAnimationFrame(run);
	});
}

/**
 * Waits for a deferred below-fold section to mount (dynamic import) then scrolls.
 */
export function scrollToMarketingAnchorWhenAvailable(
	id: string,
	hashHref?: string,
	options?: { maxWaitMs?: number },
): void {
	if (typeof window === "undefined") {
		return;
	}

	const domId = resolveMarketingAnchorDomId(id);
	const maxWaitMs = options?.maxWaitMs ?? 10_000;
	const startedAt = performance.now();

	const attempt = () => {
		const el = window.document.getElementById(domId);
		if (el) {
			scrollToMarketingAnchor(id, hashHref);
			return;
		}
		if (performance.now() - startedAt < maxWaitMs) {
			window.requestAnimationFrame(attempt);
		}
	};

	window.requestAnimationFrame(attempt);
}
